// src/pages/PermissionPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Button, 
  Space, 
  Table, 
  Tag, 
  Input, 
  Select, 
  Modal, 
  Form,
  Switch,
  message,
  Alert,
  Tree,
  Divider,
  Tooltip,
  Popconfirm,
  Badge,
  Tabs,
  List,
  Descriptions
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  LockOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined,
  SecurityScanOutlined,
  KeyOutlined,
  CrownOutlined,
  UserSwitchOutlined,
  SafetyCertificateOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  ApiOutlined,
  ControlOutlined,
  TeamOutlined,
  SaveOutlined,
  ReloadOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const PermissionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRoleModalVisible, setIsRoleModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedRole, setSelectedRole] = useState('admin');
  const [form] = Form.useForm();
  const [roleForm] = Form.useForm();

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    module: '',
    type: ''
  });

  // Modules disponibles
  const modules = [
    { key: 'dashboard', name: 'Tableau de bord', icon: <AppstoreOutlined /> },
    { key: 'clients', name: 'Clients', icon: <TeamOutlined /> },
    { key: 'vehicules', name: 'Véhicules', icon: <DatabaseOutlined /> },
    { key: 'vente', name: 'Ventes', icon: <ApiOutlined /> },
    { key: 'achat', name: 'Achats', icon: <ControlOutlined /> },
    { key: 'fournisseurs', name: 'Fournisseurs', icon: <TeamOutlined /> },
    { key: 'personnels', name: 'Personnel', icon: <UserSwitchOutlined /> },
    { key: 'profile', name: 'Profils', icon: <SecurityScanOutlined /> },
    { key: 'reports', name: 'Rapports', icon: <SafetyCertificateOutlined /> },
    { key: 'settings', name: 'Paramètres', icon: <SettingOutlined /> },
  ];

  // Types de permissions
  const permissionTypes = [
    { key: 'read', label: 'Lecture', color: 'blue' },
    { key: 'write', label: 'Écriture', color: 'green' },
    { key: 'edit', label: 'Modification', color: 'orange' },
    { key: 'delete', label: 'Suppression', color: 'red' },
    { key: 'export', label: 'Export', color: 'purple' },
    { key: 'import', label: 'Import', color: 'cyan' },
  ];

  // Rôles disponibles
  const initialRoles = [
    {
      id: 'role-admin',
      name: 'Administrateur',
      key: 'admin',
      description: 'Accès complet à toutes les fonctionnalités',
      permissions: [],
      color: 'red',
      userCount: 1
    },
    {
      id: 'role-manager',
      name: 'Manager',
      key: 'manager',
      description: 'Accès commercial et reporting',
      permissions: [],
      color: 'orange',
      userCount: 2
    },
    {
      id: 'role-user',
      name: 'Utilisateur',
      key: 'user',
      description: 'Accès opérationnel limité',
      permissions: [],
      color: 'blue',
      userCount: 15
    },
    {
      id: 'role-viewer',
      name: 'Observateur',
      key: 'viewer',
      description: 'Accès en lecture seule',
      permissions: [],
      color: 'green',
      userCount: 3
    },
  ];

  // Données initiales des permissions
  const initialPermissions = [
    { 
      id: 'perm-001', 
      name: 'Voir tableau de bord', 
      key: 'dashboard:read',
      module: 'dashboard',
      type: 'read',
      description: 'Permission de visualiser le tableau de bord',
      defaultRoles: ['admin', 'manager', 'user', 'viewer'],
      isActive: true
    },
    { 
      id: 'perm-002', 
      name: 'Gérer les clients', 
      key: 'clients:write',
      module: 'clients',
      type: 'write',
      description: 'Permission d\'ajouter et modifier des clients',
      defaultRoles: ['admin', 'manager'],
      isActive: true
    },
    { 
      id: 'perm-003', 
      name: 'Supprimer les clients', 
      key: 'clients:delete',
      module: 'clients',
      type: 'delete',
      description: 'Permission de supprimer des clients',
      defaultRoles: ['admin'],
      isActive: true
    },
    { 
      id: 'perm-004', 
      name: 'Exporter les données', 
      key: 'reports:export',
      module: 'reports',
      type: 'export',
      description: 'Permission d\'exporter les rapports',
      defaultRoles: ['admin', 'manager'],
      isActive: true
    },
    { 
      id: 'perm-005', 
      name: 'Gérer les véhicules', 
      key: 'vehicules:write',
      module: 'vehicules',
      type: 'write',
      description: 'Permission de gérer la flotte de véhicules',
      defaultRoles: ['admin', 'user'],
      isActive: true
    },
    { 
      id: 'perm-006', 
      name: 'Gérer les profils', 
      key: 'profile:write',
      module: 'profile',
      type: 'write',
      description: 'Permission de gérer les profils utilisateurs',
      defaultRoles: ['admin'],
      isActive: true
    },
  ];

  // Statistiques
  const [stats, setStats] = useState({
    totalPermissions: 0,
    activePermissions: 0,
    totalRoles: 0,
    modulesCount: 0,
    typesCount: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, permissions]);

  const fetchData = () => {
    setLoading(true);
    // Simulation API call
    setTimeout(() => {
      setPermissions(initialPermissions);
      setFilteredPermissions(initialPermissions);
      setRoles(initialRoles);
      calculateStats();
      setLoading(false);
    }, 500);
  };

  const calculateStats = () => {
    const totalPermissions = permissions.length;
    const activePermissions = permissions.filter(p => p.isActive).length;
    const totalRoles = roles.length;
    const modulesCount = modules.length;
    const typesCount = permissionTypes.length;
    
    setStats({ 
      totalPermissions, 
      activePermissions, 
      totalRoles, 
      modulesCount, 
      typesCount 
    });
  };

  const applyFilters = () => {
    let filtered = [...permissions];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(perm => 
        perm.name.toLowerCase().includes(searchLower) ||
        perm.description.toLowerCase().includes(searchLower) ||
        perm.key.toLowerCase().includes(searchLower)
      );
    }

    if (filters.module) {
      filtered = filtered.filter(perm => perm.module === filters.module);
    }

    if (filters.type) {
      filtered = filtered.filter(perm => perm.type === filters.type);
    }

    setFilteredPermissions(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const showModal = (item = null) => {
    setEditingItem(item);
    if (item) {
      form.setFieldsValue(item);
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const showRoleModal = (role) => {
    setEditingItem(role);
    roleForm.setFieldsValue(role);
    setIsRoleModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleRoleCancel = () => {
    setIsRoleModalVisible(false);
    setEditingItem(null);
    roleForm.resetFields();
  };

  const handleSubmit = (values) => {
    setLoading(true);
    
    setTimeout(() => {
      if (editingItem) {
        // Mise à jour
        const updatedPermissions = permissions.map(perm => 
          perm.id === editingItem.id ? { ...perm, ...values } : perm
        );
        setPermissions(updatedPermissions);
        message.success('Permission modifiée avec succès!');
      } else {
        // Création
        const newId = `perm-${(permissions.length + 101).toString().padStart(3, '0')}`;
        const newPermission = {
          id: newId,
          ...values,
          isActive: true,
          defaultRoles: values.defaultRoles || []
        };
        setPermissions([...permissions, newPermission]);
        message.success('Permission créée avec succès!');
      }
      
      setLoading(false);
      setIsModalVisible(false);
      form.resetFields();
      calculateStats();
    }, 500);
  };

  const handleRoleSubmit = (values) => {
    setLoading(true);
    
    setTimeout(() => {
      const updatedRoles = roles.map(role => 
        role.id === editingItem.id ? { ...role, ...values } : role
      );
      
      setRoles(updatedRoles);
      message.success('Rôle mis à jour avec succès!');
      setLoading(false);
      setIsRoleModalVisible(false);
      roleForm.resetFields();
    }, 500);
  };

  const handleDelete = (item) => {
    setLoading(true);
    
    setTimeout(() => {
      const filtered = permissions.filter(p => p.id !== item.id);
      setPermissions(filtered);
      message.success('Permission supprimée avec succès!');
      setLoading(false);
      calculateStats();
    }, 300);
  };

  const handlePermissionToggle = (permission, checked) => {
    const updatedPermissions = permissions.map(p => 
      p.id === permission.id ? { ...p, isActive: checked } : p
    );
    
    setPermissions(updatedPermissions);
    message.success(`Permission ${checked ? 'activée' : 'désactivée'}!`);
    calculateStats();
  };

  const handleRoleChange = (roleKey) => {
    setSelectedRole(roleKey);
  };

  const renderPermissionType = (type) => {
    const typeConfig = permissionTypes.find(t => t.key === type);
    return (
      <Tag color={typeConfig?.color || 'default'} className="font-medium">
        {typeConfig?.label || type}
      </Tag>
    );
  };

  const renderModule = (moduleKey) => {
    const module = modules.find(m => m.key === moduleKey);
    return (
      <div className="flex items-center gap-2">
        {module?.icon}
        <Text>{module?.name || moduleKey}</Text>
      </div>
    );
  };

  const renderRoleBadge = (roleKey) => {
    const role = roles.find(r => r.key === roleKey);
    return (
      <Tag color={role?.color || 'default'} className="font-medium">
        {role?.name || roleKey}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Permission',
      key: 'permission',
      width: 250,
      render: (_, record) => (
        <div>
          <Text strong className="block">{record.name}</Text>
          <Text type="secondary" className="text-xs block">{record.key}</Text>
          <Text type="secondary" className="text-xs">{record.description}</Text>
        </div>
      ),
    },
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      width: 120,
      render: renderModule,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: renderPermissionType,
    },
    {
      title: 'Rôles par défaut',
      dataIndex: 'defaultRoles',
      key: 'defaultRoles',
      width: 200,
      render: (rolesList) => (
        <div className="flex flex-wrap gap-1">
          {rolesList.map(roleKey => (
            <Tag key={roleKey} color="blue" size="small">
              {roleKey}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive, record) => (
        <div className="flex items-center gap-2">
          {isActive ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>Actif</Tag>
          ) : (
            <Tag color="red" icon={<CloseCircleOutlined />}>Inactif</Tag>
          )}
          <Switch
            size="small"
            checked={isActive}
            onChange={(checked) => handlePermissionToggle(record, checked)}
          />
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Modifier">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => showModal(record)}
              className="text-blue-500"
            />
          </Tooltip>
          <Popconfirm 
            title="Supprimer cette permission ?"
            description="Cette action est irréversible. Êtes-vous sûr ?"
            onConfirm={() => handleDelete(record)}
            okText="Oui"
            cancelText="Non"
            okType="danger"
          >
            <Tooltip title="Supprimer">
              <Button 
                type="link" 
                icon={<DeleteOutlined />} 
                className="text-red-500"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Préparer les données pour l'arbre de permissions
  const getTreeData = () => {
    return modules.map(module => ({
      title: (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {module.icon}
            <Text strong>{module.name}</Text>
          </div>
          <Badge 
            count={permissions.filter(p => p.module === module.key).length}
            style={{ backgroundColor: '#52c41a' }}
          />
        </div>
      ),
      key: module.key,
      children: permissionTypes.map(type => ({
        title: (
          <div className="flex items-center justify-between">
            <Tag color={type.color}>{type.label}</Tag>
            <Text type="secondary" className="text-xs">
              {permissions.filter(p => p.module === module.key && p.type === type.key).length} permissions
            </Text>
          </div>
        ),
        key: `${module.key}-${type.key}`,
        children: permissions
          .filter(p => p.module === module.key && p.type === type.key)
          .map(perm => ({
            title: (
              <div className="flex items-center justify-between">
                <Text>{perm.name}</Text>
                <Switch 
                  size="small" 
                  checked={perm.isActive}
                  onChange={(checked) => handlePermissionToggle(perm, checked)}
                />
              </div>
            ),
            key: perm.id,
          })),
      })),
    }));
  };

  return (
    <div>
      {/* En-tête */}
      <div className="mb-6">
        <Title level={2} className="m-0">Gestion des Permissions</Title>
        <Text className="text-gray-600 mt-2 block">
          Configurez les permissions et les rôles d'accès de l'application
        </Text>
      </div>

      {/* Statistiques */}
      <div className="mb-6">
        <Row gutter={[8, 8]}>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <LockOutlined className="text-blue-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Permissions totales</Text>
              <Text className="text-2xl font-bold block">{stats.totalPermissions}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <CheckCircleOutlined className="text-green-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Permissions actives</Text>
              <Text className="text-2xl font-bold text-green-600 block">{stats.activePermissions}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <CrownOutlined className="text-red-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Rôles définis</Text>
              <Text className="text-2xl font-bold text-red-600 block">{stats.totalRoles}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <AppstoreOutlined className="text-purple-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Modules</Text>
              <Text className="text-2xl font-bold text-purple-600 block">{stats.modulesCount}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <SecurityScanOutlined className="text-orange-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Types de permissions</Text>
              <Text className="text-2xl font-bold text-orange-600 block">{stats.typesCount}</Text>
            </div>
          </Col>
          <Col xs={6} sm={6} lg={4}>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-center">
              <div className="mb-2">
                <TeamOutlined className="text-gray-500 text-2xl" />
              </div>
              <Text className="text-gray-500 text-sm block">Utilisateurs assignés</Text>
              <Text className="text-2xl font-bold text-gray-600 block">
                {roles.reduce((acc, role) => acc + role.userCount, 0)}
              </Text>
            </div>
          </Col>
        </Row>
      </div>

      {/* Onglets principaux */}
      <Tabs defaultActiveKey="permissions" className="mb-6">
        <TabPane tab={<span><LockOutlined /> Permissions</span>} key="permissions">
          <Card className="modern-card mb-6">
            <Row gutter={[16, 16]} align="middle">
              <Col flex="auto">
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    className="btn-primary"
                    onClick={() => showModal()}
                  >
                    Nouvelle Permission
                  </Button>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={() => message.info('Sauvegarde des permissions')}
                  >
                    Sauvegarder
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchData}
                  >
                    Actualiser
                  </Button>
                </Space>
              </Col>
              <Col>
                <Space size="middle" wrap>
                  <Input
                    placeholder="Rechercher permission..."
                    prefix={<SearchOutlined />}
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    allowClear
                    className="w-48"
                  />
                  <Select
                    placeholder="Module"
                    value={filters.module}
                    onChange={(value) => handleFilterChange('module', value)}
                    allowClear
                    className="w-40"
                  >
                    {modules.map(module => (
                      <Option key={module.key} value={module.key}>{module.name}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="Type"
                    value={filters.type}
                    onChange={(value) => handleFilterChange('type', value)}
                    allowClear
                    className="w-40"
                  >
                    {permissionTypes.map(type => (
                      <Option key={type.key} value={type.key}>{type.label}</Option>
                    ))}
                  </Select>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Vue en tableau */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden modern-card mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <div className="flex items-center">
                <LockOutlined className="text-gray-400 mr-2" />
                <Text className="text-gray-700 font-medium">Liste des permissions</Text>
              </div>
              <Text className="text-sm text-gray-500">
                {filteredPermissions.length} permissions • {stats.activePermissions} actives
              </Text>
            </div>
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={filteredPermissions}
                rowKey="id"
                pagination={false}
                loading={loading}
                size="small"
                className="[&_.ant-table-thead>tr>th]:bg-green-700 [&_.ant-table-thead>tr>th]:py-3 [&_.ant-table-thead>tr>th]:font-medium [&_.ant-table-thead>tr>th]:text-white [&_.ant-table-tbody>tr>td]:py-3"
                rowClassName="border-b border-gray-100 hover:bg-green-50"
              />
            </div>
          </div>

          {/* Vue en arbre */}
          <Card title="Vue hiérarchique des permissions" className="mb-6">
            <Tree
              treeData={getTreeData()}
              defaultExpandAll
              showLine
              className="permission-tree"
            />
          </Card>
        </TabPane>

        <TabPane tab={<span><CrownOutlined /> Rôles</span>} key="roles">
          <Card className="modern-card mb-6">
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Text strong className="block mb-4">Sélectionnez un rôle pour voir ses permissions :</Text>
                <Space wrap>
                  {roles.map(role => (
                    <Button
                      key={role.key}
                      type={selectedRole === role.key ? 'primary' : 'default'}
                      icon={<CrownOutlined />}
                      onClick={() => handleRoleChange(role.key)}
                      style={{ 
                        borderColor: selectedRole === role.key ? role.color : undefined,
                        background: selectedRole === role.key ? role.color : undefined
                      }}
                    >
                      <Tag color={role.color}>{role.name}</Tag>
                      <Badge count={role.userCount} style={{ marginLeft: 8 }} />
                    </Button>
                  ))}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Permissions par rôle */}
          <Row gutter={[16, 16]}>
            {roles.map(role => (
              <Col xs={24} md={12} lg={6} key={role.key}>
                <Card 
                  title={
                    <div className="flex items-center justify-between">
                      <Tag color={role.color}>{role.name}</Tag>
                      <Button 
                        type="link" 
                        icon={<EditOutlined />}
                        onClick={() => showRoleModal(role)}
                        size="small"
                      />
                    </div>
                  }
                  className="h-full"
                  extra={<Badge count={role.userCount} />}
                >
                  <Text type="secondary" className="block mb-3">{role.description}</Text>
                  
                  <div className="mb-4">
                    <Text strong className="block mb-2">Permissions principales :</Text>
                    <List
                      size="small"
                      dataSource={permissions.filter(p => p.defaultRoles.includes(role.key)).slice(0, 5)}
                      renderItem={perm => (
                        <List.Item className="p-0 py-1">
                          <div className="flex items-center justify-between w-full">
                            <Text className="text-xs">{perm.name}</Text>
                            <Switch size="small" checked={perm.isActive} />
                          </div>
                        </List.Item>
                      )}
                    />
                    {permissions.filter(p => p.defaultRoles.includes(role.key)).length > 5 && (
                      <Text type="secondary" className="text-xs mt-2">
                        + {permissions.filter(p => p.defaultRoles.includes(role.key)).length - 5} autres permissions
                      </Text>
                    )}
                  </div>

                  <Button 
                    type="link" 
                    size="small"
                    onClick={() => showRoleModal(role)}
                    block
                  >
                    Gérer ce rôle
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </TabPane>

        <TabPane tab={<span><SettingOutlined /> Configuration</span>} key="settings">
          <Card title="Configuration des modules" className="mb-6">
            <Row gutter={[16, 16]}>
              {modules.map(module => (
                <Col xs={24} sm={12} md={8} lg={6} key={module.key}>
                  <Card 
                    size="small"
                    className="text-center hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/profile/permissions/module/${module.key}`)}
                  >
                    <div className="mb-3">
                      {React.cloneElement(module.icon, { className: 'text-2xl text-primary-500' })}
                    </div>
                    <Text strong className="block mb-1">{module.name}</Text>
                    <Text type="secondary" className="text-xs">
                      {permissions.filter(p => p.module === module.key).length} permissions
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>

          <Card title="Types de permissions" className="mb-6">
            <Row gutter={[16, 16]}>
              {permissionTypes.map(type => (
                <Col xs={24} sm={12} md={8} lg={4} key={type.key}>
                  <div className="text-center p-4 border rounded-lg">
                    <Tag color={type.color} className="mb-2">
                      {type.label}
                    </Tag>
                    <Text className="block text-sm font-medium">{type.key}</Text>
                    <Text type="secondary" className="text-xs">
                      {permissions.filter(p => p.type === type.key).length} permissions
                    </Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </TabPane>
      </Tabs>

      {/* Modal pour ajouter/modifier une permission */}
      <Modal
        title={editingItem ? "Modifier la Permission" : "Nouvelle Permission"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          className="modern-form"
          onFinish={handleSubmit}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Nom de la permission"
                rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
              >
                <Input placeholder="Ex: Gérer les clients" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="key"
                label="Clé technique"
                rules={[{ required: true, message: 'Veuillez saisir la clé technique' }]}
              >
                <Input placeholder="Ex: clients:write" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="module"
                label="Module"
                rules={[{ required: true, message: 'Veuillez sélectionner le module' }]}
              >
                <Select placeholder="Sélectionnez le module">
                  {modules.map(module => (
                    <Option key={module.key} value={module.key}>
                      <div className="flex items-center gap-2">
                        {module.icon}
                        {module.name}
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type de permission"
                rules={[{ required: true, message: 'Veuillez sélectionner le type' }]}
              >
                <Select placeholder="Sélectionnez le type">
                  {permissionTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      <Tag color={type.color}>{type.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Veuillez saisir une description' }]}
          >
            <Input.TextArea 
              placeholder="Description détaillée de la permission..." 
              rows={2}
            />
          </Form.Item>

          <Form.Item
            name="defaultRoles"
            label="Rôles par défaut"
          >
            <Select
              mode="multiple"
              placeholder="Sélectionnez les rôles"
              allowClear
              style={{ width: '100%' }}
            >
              {roles.map(role => (
                <Option key={role.key} value={role.key}>
                  <Tag color={role.color}>{role.name}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Statut"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" defaultChecked />
          </Form.Item>

          <Alert
            message="Information"
            description="La clé technique doit être unique et suivre le format 'module:action' (ex: clients:read, vehicules:write)"
            type="info"
            showIcon
            className="mb-4"
          />

          <div className="text-right mt-6">
            <Space>
              <Button onClick={handleCancel}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} className="btn-primary">
                {editingItem ? 'Modifier' : 'Créer'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal pour gérer un rôle */}
      <Modal
        title={`Gérer le rôle - ${editingItem?.name}`}
        open={isRoleModalVisible}
        onCancel={handleRoleCancel}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={roleForm}
          layout="vertical"
          className="modern-form"
          onFinish={handleRoleSubmit}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Nom du rôle"
                rules={[{ required: true, message: 'Veuillez saisir le nom du rôle' }]}
              >
                <Input placeholder="Ex: Manager Commercial" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="key"
                label="Clé technique"
                rules={[{ required: true, message: 'Veuillez saisir la clé technique' }]}
              >
                <Input placeholder="Ex: manager" disabled={editingItem?.key === 'admin'} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Veuillez saisir une description' }]}
          >
            <Input.TextArea 
              placeholder="Description du rôle et de ses responsabilités..." 
              rows={2}
            />
          </Form.Item>

          <Card title="Permissions attribuées" size="small" className="mb-4">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Permissions totales">
                {permissions.filter(p => p.defaultRoles.includes(editingItem?.key)).length}
              </Descriptions.Item>
              <Descriptions.Item label="Permissions actives">
                {permissions.filter(p => p.defaultRoles.includes(editingItem?.key) && p.isActive).length}
              </Descriptions.Item>
              <Descriptions.Item label="Modules couverts" span={2}>
                <Space wrap>
                  {Array.from(new Set(
                    permissions
                      .filter(p => p.defaultRoles.includes(editingItem?.key))
                      .map(p => p.module)
                  )).map(moduleKey => {
                    const module = modules.find(m => m.key === moduleKey);
                    return module ? (
                      <Tag key={moduleKey} color="blue">
                        {module.name}
                      </Tag>
                    ) : null;
                  })}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Alert
            message="Attention"
            description="Les modifications apportées à ce rôle affecteront tous les utilisateurs qui lui sont assignés."
            type="warning"
            showIcon
            className="mb-4"
          />

          <div className="text-right mt-6">
            <Space>
              <Button onClick={handleRoleCancel}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} className="btn-primary">
                Enregistrer les modifications
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionPage;